import {
  EMPTY_ARRAY,
  getAgGridColId,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type DateParams,
  type FixingIndexMarkMessageData,
} from '@talos/kyoko';
import { useMemo } from 'react';

interface UseIndexMarksColumnsProps {
  defaultColumns?: (keyof FixingIndexMarkMessageData | Partial<Column>)[];
}

const DEFAULT_COLUMNS: (keyof FixingIndexMarkMessageData | Partial<Column>)[] = [
  'Timestamp',
  'NominalTime',
  { id: 'NominalTimeInIndexTZ' },
  'IndexID',
  'Index',
  'Mark',
  'Source',
  'OrgID',
  'Revision',
  'UpdateAction',
  'ID',
];

export function useIndexMarksColumns({ defaultColumns = DEFAULT_COLUMNS }: UseIndexMarksColumnsProps) {
  const columnDefinitions = useMemo(
    () =>
      new Map<string, ColumnDef<FixingIndexMarkMessageData>>(
        (
          [
            { field: 'Timestamp', type: 'date', title: 'Timestamp', initialSort: '-' },
            {
              id: 'NominalTime',
              field: 'NominalTime',
              type: 'date',
              title: 'Nominal Time (local)',
            },
            {
              id: 'NominalTimeInIndexTZ',
              field: 'NominalTime',
              type: 'date',
              title: 'Nominal Time (Index TZ)',
              initialWidth: 170,
              params: {
                timezone: (data, context) =>
                  data ? context.current.fixingIndicesByID?.get(data.IndexID)?.NominalIndexTimeZone : undefined,
              } satisfies DateParams<FixingIndexMarkMessageData>,
            },
            {
              field: 'IndexID',
              id: 'IndexID',
              type: 'text',
              title: 'Fixing Index ID',
            },
            {
              field: 'Index',
              type: 'text',
              title: 'Index',
              description: 'The Fixing Index as seen on the IndexMark item.',
            },
            {
              field: 'IndexID',
              id: 'IndexDisplayName',
              type: 'fixingIndex',
              title: 'Fixing Index',
              description:
                'Resolved from the Fixing Index feed. A value confirms the mark is tied to a known index; empty indicates the IndexID has no matching FixingIndex.',
              enableRowGroup: true,
              params: { valueType: 'IndexID' },
            },
            { field: 'Mark', type: 'text', title: 'Mark' },
            { field: 'Source', type: 'text', title: 'Source' },
            { field: 'OrgID', type: 'text', title: 'Org ID', initialHide: true },
            { field: 'Revision', type: 'text', title: 'Revision' },
            { field: 'UpdateAction', type: 'text', title: 'Update Action', initialHide: true },
            { field: 'UpdateUser', type: 'user', title: 'Update User', initialHide: true },
            { field: 'ID', type: 'text', title: 'ID' },
          ] satisfies ColumnDef<FixingIndexMarkMessageData>[]
        ).map(column => [getAgGridColId(column), column])
      ),
    []
  );

  return useDefaultColumns(defaultColumns ?? EMPTY_ARRAY, columnDefinitions);
}
